// SitrepButton.jsx — MINSKY-16407: "Generate Situation Report" trigger + modal.
//
// UX mirrors AgenticSectionControls' input+submit pattern (InputBase-style free-text field,
// IconButton submit with a busy spinner, inline error text) — see that file's `isBusy`/
// `isSubmitting` state machine — adapted here into a small Dialog since this control needs two
// fields (additional context + lookback window) rather than one, plus an explicit
// open/close affordance separate from the map-node header's other always-visible controls.
//
// This component owns its own open/busy/error state locally (useState) rather than being
// controlled by MapNodeChrome, since — unlike the view/DTG/basemap fields — nothing outside this
// component ever needs to read or drive that state: there's no Yjs-backed value to keep in sync
// with, just a one-shot "collect two inputs, call a callback" action.
//
// Phase 1 (MINSKY-16404): `onGenerateSitrep` builds the payload and console.logs it — no LLM
// call, no ProseMirror mutation. See MapNodeView._onGenerateSitrep / buildSitrepPayload.js.

import React, {useState} from 'react';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';

const DEFAULT_WINDOW_DAYS = 7;

export function SitrepButton({onGenerateSitrep, dialogPaperRef, disabled = false}) {
    const [open, setOpen] = useState(false);
    const [additionalContext, setAdditionalContext] = useState('');
    const [windowDays, setWindowDays] = useState(DEFAULT_WINDOW_DAYS);
    const [maxCheckpoints, setMaxCheckpoints] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const handleOpen = () => {
        setError(null);
        setOpen(true);
    };

    const handleClose = () => {
        if (busy) return; // don't let a stray click close the dialog mid-submit
        setOpen(false);
    };

    const handleSubmit = async () => {
        if (busy) return;
        setBusy(true);
        setError(null);
        try {
            const parsedWindowDays = Number(windowDays);
            const parsedMaxCheckpoints = maxCheckpoints === '' ? null : Number(maxCheckpoints);
            await onGenerateSitrep({
                additionalContext: additionalContext.trim(),
                windowDays: Number.isFinite(parsedWindowDays) && parsedWindowDays > 0
                    ? parsedWindowDays : DEFAULT_WINDOW_DAYS,
                maxCheckpoints: Number.isFinite(parsedMaxCheckpoints) && parsedMaxCheckpoints > 0
                    ? parsedMaxCheckpoints : null,
            });
            setOpen(false);
            setAdditionalContext('');
        } catch (err) {
            setError(err?.message || 'Failed to generate situation report.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <Tooltip
                title={disabled ? 'A situation report is already generating for this map' : 'Summarise all markings and recent changes into a written situation report'}
            >
                <span>
                    <Button
                        size="small"
                        variant="text"
                        onClick={handleOpen}
                        disabled={disabled}
                        aria-label="Generate situation report"
                        data-testid="pm-map-node-sitrep-trigger"
                        startIcon={<SummarizeOutlinedIcon fontSize="small"/>}
                        sx={{flexShrink: 0, whiteSpace: 'nowrap'}}
                    >
                        Sitrep
                    </Button>
                </span>
            </Tooltip>

            <Dialog
                open={open}
                onClose={handleClose}
                fullWidth
                maxWidth="sm"
                PaperProps={{ref: dialogPaperRef}}
            >
                <DialogTitle>Generate Situation Report</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{mt: 0.5}}>
                        <Typography variant="body2" color="text.secondary">
                            Generates a written situation report from this map's current markings
                            and recent movement history. The report will be added as a draft section
                            in your document for review.
                        </Typography>
                        <TextField
                            label="Additional context (optional)"
                            placeholder="e.g. focus of effort, unconfirmed reports…"
                            multiline
                            minRows={2}
                            fullWidth
                            value={additionalContext}
                            onChange={(e) => setAdditionalContext(e.target.value)}
                            inputProps={{'data-testid': 'pm-map-node-sitrep-context', 'aria-label': 'Additional context'}}
                            disabled={busy}
                        />
                        <Stack direction="row" spacing={2}>
                            <TextField
                                label="Lookback window (days)"
                                type="number"
                                value={windowDays}
                                onChange={(e) => setWindowDays(e.target.value)}
                                inputProps={{
                                    min: 1, 'data-testid': 'pm-map-node-sitrep-window-days',
                                    'aria-label': 'Lookback window in days',
                                }}
                                disabled={busy}
                                sx={{flex: 1}}
                            />
                            <TextField
                                label="Max checkpoints (optional)"
                                type="number"
                                value={maxCheckpoints}
                                onChange={(e) => setMaxCheckpoints(e.target.value)}
                                inputProps={{
                                    min: 1, 'data-testid': 'pm-map-node-sitrep-max-checkpoints',
                                    'aria-label': 'Maximum number of checkpoints',
                                }}
                                disabled={busy}
                                sx={{flex: 1}}
                            />
                        </Stack>
                        {error ? (
                            <Typography variant="body2" color="error">{error}</Typography>
                        ) : null}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={busy}>Cancel</Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={busy}
                        startIcon={busy ? <CircularProgress size={14} color="inherit"/> : null}
                        data-testid="pm-map-node-sitrep-submit"
                    >
                        Generate
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
